/**
 * Project Detail → Party tab. 1:1 with the folder's `ptab-party`.
 *
 * Layout:
 *   Top row (grid 300px | 1fr)
 *     - Team chip card (left)
 *     - Summary metrics strip (right) — Advance Paid + To Receive
 *   Parties list card
 *     - Header row: `Parties · {count}` + `+ New Party`
 *     - `.party-row` per party with balance pill (to-receive / to-pay / neutral)
 *
 * Real data:
 *   - Project parties derived via `useProjectParties` (union of party
 *     ids referenced by this project's transactions)
 *   - Per-party balance from posted transactions (income − expense)
 *   - "Advance Paid" = sum of payment_out across all project txns
 *   - "To Receive" = sum of payment_in across all project txns
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { canManageTeam, canPartyWrite } from '@shared/perms';

import { PartyFormSlideOver } from '@/components/PartyFormSlideOver';
import { PartyPickerSlideOver } from '@/components/PartyPickerSlideOver';
import { TeamMembersSlideOver } from '@/components/TeamMembersSlideOver';
import {
  formatIndianPhone,
  partyTypeColors,
  partyTypeLabel,
  type Party,
} from '@/lib/parties';
import { linkPartyOnTransactions } from '@/lib/transactions';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import { useProjectMembers, type ProjectMember } from '@/lib/useProjectMembers';
import { useProjectParties } from '@/lib/useProjectParties';
import {
  isCountedInTotals,
  normaliseTxType,
} from '@/lib/useProjectTransactions';
import type { ProjectListItem } from '@/lib/useProjects';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0] ?? '').join('').toUpperCase() || '?';
}

function compactInr(n: number): string {
  if (!n) return '₹0';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(2)}L`;
  if (n >= 1000)     return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${n.toLocaleString('en-IN')}`;
}

function formatInr(n: number): string {
  return `₹${n.toLocaleString('en-IN')}`;
}

export function PartyTab({
  project,
  onOpenTransaction: _onOpenTransaction,
}: {
  project: ProjectListItem;
  /** Kept on the API for the parent's tab wiring; no longer used —
   *  party rows now navigate to the full Party Detail page scoped
   *  to this project, where transactions are opened in-page. */
  onOpenTransaction?: (txnId: string) => void;
}) {
  const navigate = useNavigate();
  const orgId = project.orgId ?? null;
  const { role: orgRole } = useCurrentOrgRole();
  const canManageTeamMembers = canManageTeam(orgRole);
  const canWriteParty = canPartyWrite(orgRole);
  const { parties, unlinked, loading, error, transactions } = useProjectParties(
    orgId,
    project.id,
  );
  // Real project team (staff + clients on this project), distinct from
  // transactional parties above. Drives the team chip card.
  const { members } = useProjectMembers(project.id);

  // Top-of-tab summary (project-scoped, posted only).
  const summary = useMemo(() => {
    let inSum = 0;
    let outSum = 0;
    for (const t of transactions) {
      if (!isCountedInTotals(t)) continue;
      const k = normaliseTxType(t.type);
      if (k === 'in') inSum += t.amount ?? 0;
      else if (k === 'out') outSum += t.amount ?? 0;
    }
    return { advancePaid: outSum, toReceive: inSum };
  }, [transactions]);

  // Slide-over state (form + team + party picker for linking
  // unlinked transactions to a party doc). Preview happens on the
  // dedicated Party Detail page (`/parties/:id?projectId=…`).
  const [teamOpen, setTeamOpen] = useState(false);
  const [formMode, setFormMode] = useState<
    | { kind: 'closed' }
    | { kind: 'create' }
    | { kind: 'edit'; party: Party }
  >({ kind: 'closed' });
  const [linkTarget, setLinkTarget] = useState<{
    nameKey: string;
    displayName: string;
  } | null>(null);
  const [linking, setLinking] = useState(false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Body — parties list (left, wide) + right rail (300px) with
          summary cards stacked above the Team card. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 300px',
          gap: 24,
          alignItems: 'start',
        }}
      >
        <div
          className="card"
          style={{
            padding: 0,
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            overflow: 'hidden',
          }}
        >
        <div
          style={{
            padding: '16px 20px',
            background: '#fff',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Parties · {parties.length + unlinked.length}
            {unlinked.length > 0 ? (
              <span
                style={{
                  marginLeft: 8,
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '2px 6px',
                  borderRadius: 4,
                  background: 'var(--warning-bg)',
                  color: 'var(--warning-text)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                }}
              >
                {unlinked.length} unlinked
              </span>
            ) : null}
          </div>
          {canWriteParty && (
            <button
              type="button"
              className="btn-primary"
              style={{ height: 32, fontSize: 12, padding: '0 12px' }}
              onClick={() => setFormMode({ kind: 'create' })}
            >
              <i className="ph-bold ph-plus" /> New Party
            </button>
          )}
        </div>

        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)' }}>
            Loading parties…
          </div>
        ) : error ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--critical)' }}>
            Couldn't load: {error}
          </div>
        ) : parties.length === 0 && unlinked.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>
              <i className="ph ph-address-book" />
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
              No parties on this project yet
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--text-secondary)',
                maxWidth: 360,
                margin: '0 auto 16px',
              }}
            >
              Parties show up here once you log a transaction with one. Add a party from the New Party button, then record an entry on the Transactions tab.
            </div>
            {canWriteParty && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setFormMode({ kind: 'create' })}
              >
                <i className="ph-bold ph-plus" /> Add a party
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Linked rows — parties with a real partyId */}
            {parties.map(({ party, balance }) => {
              const colors = partyTypeColors(party.partyType);
              const variant =
                balance.balance > 0
                  ? 'to-receive'
                  : balance.balance < 0
                    ? 'to-pay'
                    : 'neutral';
              const label =
                variant === 'to-receive'
                  ? 'TO RECEIVE'
                  : variant === 'to-pay'
                    ? 'TO PAY'
                    : 'SETTLED';
              return (
                <div
                  key={party.id}
                  className="party-row"
                  onClick={() =>
                    navigate(
                      `/parties/${party.id}?projectId=${project.id}`,
                    )
                  }
                >
                  <div
                    className="party-avatar"
                    style={{ background: colors.bg, color: colors.fg }}
                  >
                    {initials(party.name)}
                  </div>
                  <div className="party-info">
                    <div className="party-name">{party.name}</div>
                    <div className="party-meta">
                      <span className="party-type">
                        {partyTypeLabel(party.partyType)}
                      </span>
                      <span className="party-divider">·</span>
                      <span>{formatIndianPhone(party.phone)}</span>
                      {balance.txnCount > 0 ? (
                        <>
                          <span className="party-divider">·</span>
                          <span className="party-txns">
                            {balance.txnCount} {balance.txnCount === 1 ? 'txn' : 'txns'}
                          </span>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <div className={`party-balance ${variant}`}>
                    <div className="balance-label">{label}</div>
                    <div className="balance-amount">
                      {compactInr(Math.abs(balance.balance))}
                    </div>
                  </div>
                  <div className="party-chevron">
                    <i className="ph-bold ph-caret-right" />
                  </div>
                </div>
              );
            })}

            {/* Unlinked rows — transactions with typed partyName but
                no partyId. Click → opens picker to link or create. */}
            {unlinked.map((row) => {
              const variant =
                row.balance.balance > 0
                  ? 'to-receive'
                  : row.balance.balance < 0
                    ? 'to-pay'
                    : 'neutral';
              const label =
                variant === 'to-receive'
                  ? 'TO RECEIVE'
                  : variant === 'to-pay'
                    ? 'TO PAY'
                    : 'SETTLED';
              return (
                <div
                  key={row.id}
                  className="party-row"
                  onClick={() =>
                    setLinkTarget({
                      nameKey: row.nameKey,
                      displayName: row.name,
                    })
                  }
                  title="Click to link this name to a party"
                >
                  <div
                    className="party-avatar"
                    style={{
                      background: '#FAFAFA',
                      color: 'var(--text-tertiary)',
                      border: '1.5px dashed var(--text-tertiary)',
                    }}
                  >
                    {initials(row.name)}
                  </div>
                  <div className="party-info">
                    <div className="party-name">{row.name}</div>
                    <div className="party-meta">
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          padding: '2px 6px',
                          borderRadius: 4,
                          background: 'var(--warning-bg)',
                          color: 'var(--warning-text)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.4px',
                        }}
                      >
                        Unlinked
                      </span>
                      <span className="party-divider">·</span>
                      <span className="party-txns">
                        {row.balance.txnCount}{' '}
                        {row.balance.txnCount === 1 ? 'txn' : 'txns'}
                      </span>
                      <span className="party-divider">·</span>
                      <span style={{ color: 'var(--brand)', fontWeight: 600 }}>
                        Link / Create
                      </span>
                    </div>
                  </div>
                  <div className={`party-balance ${variant}`}>
                    <div className="balance-label">{label}</div>
                    <div className="balance-amount">
                      {compactInr(Math.abs(row.balance.balance))}
                    </div>
                  </div>
                  <div className="party-chevron">
                    <i className="ph-bold ph-caret-right" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </div>

        {/* RIGHT rail — Summary cards stacked above Team card */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            position: 'sticky',
            top: 16,
          }}
        >
          {/* Advance Paid */}
          <div
            className="card"
            style={{
              padding: 16,
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
                marginBottom: 6,
              }}
            >
              Advance Paid
            </div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: 'var(--critical)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatInr(summary.advancePaid)}
            </div>
          </div>

          {/* To Receive */}
          <div
            className="card"
            style={{
              padding: 16,
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
                marginBottom: 6,
              }}
            >
              To Receive
            </div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: 'var(--success)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatInr(summary.toReceive)}
            </div>
          </div>

          {/* Team list */}
          <TeamRailCard
            members={members}
            onManage={() => setTeamOpen(true)}
            canManage={canManageTeamMembers}
          />
        </div>
      </div>

      {/* Form slide-over */}
      <PartyFormSlideOver
        open={formMode.kind !== 'closed'}
        onClose={() => setFormMode({ kind: 'closed' })}
        orgId={orgId}
        party={formMode.kind === 'edit' ? formMode.party : null}
        onSaved={(id) => {
          setFormMode({ kind: 'closed' });
          navigate(`/parties/${id}?projectId=${project.id}`);
        }}
      />

      {/* Team members slide-over (staff + clients on this project) */}
      <TeamMembersSlideOver
        open={teamOpen}
        onClose={() => setTeamOpen(false)}
        orgId={orgId}
        projectId={project.id}
      />

      {/* Party picker — opened when the customer clicks an unlinked
          row. Lets them pick an existing party or create a new one;
          the chosen partyId is then backfilled across every project
          transaction matching the typed name. */}
      <PartyPickerSlideOver
        open={Boolean(linkTarget)}
        onClose={() => {
          if (!linking) setLinkTarget(null);
        }}
        orgId={orgId}
        defaultName={linkTarget?.displayName ?? ''}
        onPick={async (party) => {
          if (!orgId || !linkTarget) return;
          setLinking(true);
          try {
            const count = await linkPartyOnTransactions({
              orgId,
              projectId: project.id,
              nameKey: linkTarget.nameKey,
              partyName: party.name,
              partyId: party.id,
            });
            setLinkTarget(null);
            // Soft confirmation via console only — the row vanishing
            // from the unlinked list IS the confirmation the user sees.
            console.info(
              `[PartyTab] Linked ${count} transaction${count === 1 ? '' : 's'} to ${party.name}.`,
            );
          } catch (e) {
            console.warn('[PartyTab] linkPartyOnTransactions failed:', e);
            alert(
              `Could not link party: ${(e as Error).message ?? 'unknown error'}`,
            );
          } finally {
            setLinking(false);
          }
        }}
      />
    </div>
  );
}

// ───── Team rail card (right column, list view) ─────
//
// Note: a `TeamAvatars` overlap-chip component used to live here
// for a planned project-header summary row. It was never wired up
// and was blocking `tsc -b` via noUnusedLocals — deleted in the
// staff-advance ship. Recoverable from git history if the header
// summary lands later.

const ROLE_LABEL_SHORT: Record<string, string> = {
  superAdmin: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  accountant: 'Accountant',
  siteEngineer: 'Site Engr',
  supervisor: 'Supervisor',
  designer: 'Designer',
  viewer: 'Viewer',
  client: 'Client',
};

function TeamRailCard({
  members,
  onManage,
  canManage,
}: {
  members: ProjectMember[];
  onManage: () => void;
  // Hidden for roles that can't write — Firestore rule blocks it anyway
  canManage: boolean;
}) {
  return (
    <div
      className="card"
      style={{
        padding: 0,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#fff',
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Team · {members.length}
        </div>
        <button
          type="button"
          onClick={onManage}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--brand)',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            padding: 0,
          }}
        >
          Manage
        </button>
      </div>

      {/* List */}
      {members.length === 0 ? (
        canManage ? (
          // Hidden for roles that can't write — Firestore rule blocks it anyway
          <button
            type="button"
            onClick={onManage}
            style={{
              width: '100%',
              padding: '24px 16px',
              textAlign: 'center',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              font: 'inherit',
            }}
          >
            <div style={{ fontSize: 24, color: 'var(--text-tertiary)', marginBottom: 6 }}>
              <i className="ph ph-users-three" />
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
              Add team
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
              Invite people who'll work on this project.
            </div>
          </button>
        ) : null
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {members.slice(0, 8).map((m) => {
            const key = m.isProjectClient ? 'client' : (m.role ?? 'viewer');
            const c = TEAM_ROLE_TINTS[key] ?? TEAM_ROLE_TINTS.viewer;
            return (
              <button
                key={m.uid}
                type="button"
                onClick={onManage}
                title={m.displayName}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 16px',
                  border: 'none',
                  borderBottom: '1px solid var(--border)',
                  background: '#fff',
                  cursor: 'pointer',
                  font: 'inherit',
                  textAlign: 'left',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: c.bg,
                    color: c.fg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: 11,
                    flexShrink: 0,
                  }}
                >
                  {initials(m.displayName)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: 'var(--text-primary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {m.displayName}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: c.fg,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                    }}
                  >
                    {ROLE_LABEL_SHORT[key] ?? 'Member'}
                  </div>
                </div>
              </button>
            );
          })}
          {members.length > 8 ? (
            <button
              type="button"
              onClick={onManage}
              style={{
                padding: '10px 16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 12,
                color: 'var(--brand)',
                fontWeight: 600,
                textAlign: 'center',
                font: 'inherit',
              }}
            >
              + {members.length - 8} more
            </button>
          ) : canManage ? (
            // Hidden for roles that can't write — Firestore rule blocks it anyway
            <button
              type="button"
              onClick={onManage}
              style={{
                padding: '10px 16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 12,
                color: 'var(--brand)',
                fontWeight: 600,
                textAlign: 'left',
                font: 'inherit',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <i className="ph-bold ph-plus" /> Add member
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

// ───── Overlapping team-member avatars (project staff + clients) ─────

const TEAM_ROLE_TINTS: Record<string, { bg: string; fg: string }> = {
  superAdmin: { bg: '#FEF2F2', fg: '#B91C1C' },
  admin: { bg: '#FEF3C7', fg: '#92400E' },
  manager: { bg: '#DBEAFE', fg: '#1D4ED8' },
  accountant: { bg: '#E0E7FF', fg: '#3730A3' },
  siteEngineer: { bg: '#D1FAE5', fg: '#065F46' },
  supervisor: { bg: '#FFEDD5', fg: '#9A3412' },
  designer: { bg: '#FCE7F3', fg: '#BE185D' },
  viewer: { bg: '#F3F4F6', fg: '#374151' },
  client: { bg: '#F5F3FF', fg: '#6D28D9' },
};

// Note: a `TeamMemberAvatars` chip-stack component lived here
// for a planned header summary. Unused + blocking `tsc -b`
// (noUnusedLocals) — deleted in the staff-advance ship.
// Recoverable from git history if the header summary lands.
